import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { AccountAppCommand, AccountAppValue, Reward, RewardStateFilterType } from "@fabric-types/app-account";
import { Store } from "@ngrx/store";
import { CommonState } from "@obg/ngx.common";
import { AppContextSelector, BonusContext, ProductType, WalletService } from "@obg/ngx.core";
import { TranslationService } from "@obg/ngx.core.content";
import { DocumentRef, fromSecondsToMilliseconds } from "@obg/ngx.global";
import { ignoreNil } from "@obg/rxjs";
import { Observable, Subscription, timer } from "rxjs";
import { switchMap, tap, withLatestFrom } from "rxjs/operators";

import { BONUS_PROGRESS_LABEL_KEY, BONUS_PROGRESS_POLLING_INTERVAL } from "./bonus-progress.const";

@Component({
	selector: "obg-m-bonus-progress",
	templateUrl: "./bonus-progress.component.html",
	styleUrls: ["./bonus-progress.component.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BonusProgressComponent implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-bonus-progress") hostCssClass = true;

	reward: Reward | undefined;
	percentageComplete: string | undefined;

	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CommonState>,
		private translationService: TranslationService,
		private changeDetector: ChangeDetectorRef,
		private walletService: WalletService,
		private documentRef: DocumentRef
	) {}

	ngOnInit() {
		this.data$$ = timer(0, fromSecondsToMilliseconds(BONUS_PROGRESS_POLLING_INTERVAL))
			.pipe(
				tap(() => this.documentRef.obgEventBus?.dispatch(AccountAppCommand.ClearRewards)),
				switchMap(
					() =>
						new Observable(
							this.documentRef.obgEventBus.retrieve(AccountAppValue.GetRewards, {
								rewardStateFilterType: [RewardStateFilterType.Active],
							})
						)
				),
				ignoreNil(),
				withLatestFrom(this.store.select(AppContextSelector.getProduct)),
				tap(([activeRewards, productType]) => {
					const productBonusContext = this.getBonusContext(productType!);
					const rewards = activeRewards
						.filter(
							(reward) =>
								this.checkInitialAndWageredAmounts(reward) &&
								this.checkWageringPercentage(reward) &&
								this.checkBonusContext(reward, productBonusContext)
						)
						.sort(this.compareByExpiryDate);
					this.reward = rewards.length ? rewards[0] : undefined;

					if (!this.reward) {
						return;
					}

					const percentage = this.getPercentage(this.reward).toString();
					this.percentageComplete = this.translationService.getByKey(BONUS_PROGRESS_LABEL_KEY, {
						percentage,
					});
				})
			)
			.subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	private getBonusContext(productType: ProductType) {
		return this.walletService.getBonusContext(productType);
	}

	private checkBonusContext(reward: Reward, productBonusContext: BonusContext): boolean {
		return reward.bonusContext.includes(productBonusContext);
	}

	private checkWageringPercentage(reward: Reward): boolean {
		return reward.percentageComplete !== undefined && reward.percentageComplete < 100;
	}

	private checkInitialAndWageredAmounts(reward: Reward): boolean {
		return reward.amountWagered !== undefined && reward.initialBonusAmount !== undefined;
	}

	private compareByExpiryDate(rewardA: Reward, rewardB: Reward): number {
		return Date.parse(rewardA.expiryDate!) - Date.parse(rewardB.expiryDate!);
	}

	private getPercentage(reward: Reward): number {
		return Math.round((reward.amountWagered! / reward.initialBonusAmount!) * 10000) / 100;
	}
}
